import {
  doAsync,
  localStorageGetParsedItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from '@aboutyou/utils';
import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {createSharedStore} from '../sharedStore';
import type {StoreApi, UseBoundStore} from 'zustand';
import {getIsConsentBannerAccepted} from '../utils/consentBannerAcceptedState';
import {getValueToPersist} from '../utils/getValueToPersist';

export const EMPTY_VALUE = Symbol('EMPTY_VALUE');

type StoredValueSlice = {
  value?: unknown;
  valueAfterCookieConsent?: unknown;
};

interface LocalStorageStoreState {
  store: Record<string, StoredValueSlice>;
  update: (slice: Record<string, StoredValueSlice>) => void;
}

type ContextValue = {
  useStoreBase: UseBoundStore<StoreApi<LocalStorageStoreState>>;
  updateLocalStorage: <Value>(options: {
    key: string;
    nextValue: (Value | undefined) | ((value: Value | undefined) => Value);
    forceStoreDefaultValuesToLocalStorage?: boolean;
    forcePersistValue_ILLEGALLY?: boolean;
  }) => void;
  addKeyToIgnoreUpdatesOnOtherTabsList: (key: string) => void;
};

const LocalStorageContext = createContext<ContextValue>(
  new Proxy<ContextValue>({} as ContextValue, {
    get() {
      throw new Error(
        'Cannot find value for LocalStorageContext. Did you add a LocalStorageProvider in one of the parent components?',
      );
    },
  }),
);

export const useBaseStore = () => {
  const useStoreBase = useContext(LocalStorageContext).useStoreBase;
  return useStoreBase;
};

export const useStoredValue = (key: string) => {
  const storeValue = useBaseStore()(state => state.store[key]);
  return storeValue?.valueAfterCookieConsent ?? storeValue?.value;
};

export const useUpdateStoreValue = () => {
  const updateLocalStorage = useContext(LocalStorageContext).updateLocalStorage;
  return updateLocalStorage;
};

export const useAddKeyToIgnoreUpdatesOnOtherTabsList = () => {
  const addKeyToIgnoreUpdatesOnOtherTabs =
    useContext(LocalStorageContext).addKeyToIgnoreUpdatesOnOtherTabsList;
  return addKeyToIgnoreUpdatesOnOtherTabs;
};

const keysWhereUpdatesOnOtherTabsAreIgnored = new Set<string>();

export function LocalStorageProvider({children}: PropsWithChildren) {
  const useStoreBase = useRef(
    createSharedStore<LocalStorageStoreState>(set => ({
      store: {},
      update: slice => {
        return set(state => ({store: {...state.store, ...slice}}));
      },
    })),
  ).current;

  const updateLocalStorage: ContextValue['updateLocalStorage'] = useCallback(
    async ({key, nextValue, forcePersistValue_ILLEGALLY}) => {
      if (typeof nextValue === 'function') {
        const currentState = useStoreBase.getState().store[key];
        const currentValue =
          currentState?.valueAfterCookieConsent ?? currentState?.value;

        nextValue = (nextValue as Function)(currentValue);
      }

      const valueBeforeCookieConsent =
        useStoreBase.getState().store[key]?.value;
      if (nextValue === undefined) {
        localStorageRemoveItem(key);
        useStoreBase.getState().update({
          [key]: {value: EMPTY_VALUE, valueAfterCookieConsent: EMPTY_VALUE},
        });
        return;
      }

      const canPersist =
        getIsConsentBannerAccepted() || forcePersistValue_ILLEGALLY;

      const valueToStore: StoredValueSlice = {
        value: canPersist ? valueBeforeCookieConsent : nextValue,
        valueAfterCookieConsent: canPersist ? nextValue : undefined,
      };
      const currentValue = useStoreBase.getState().store[key];

      if (
        valueToStore.value === currentValue?.value &&
        valueToStore.valueAfterCookieConsent ===
          currentValue?.valueAfterCookieConsent
      ) {
        return;
      }

      const stateSlice = {[key]: valueToStore};
      useStoreBase.getState().update(stateSlice);

      if (canPersist) {
        doAsync(() => {
          localStorageSetItem(
            key,
            getValueToPersist({
              valueBeforeCookieConsent,
              valueAfterCookieConsent: valueToStore.valueAfterCookieConsent,
            }),
          );
        });
      }
    },
    [useStoreBase],
  );

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const listener = ({key}: StorageEvent) => {
      if (!getIsConsentBannerAccepted()) {
        return;
      }
      if (!key) {
        return;
      }
      if (keysWhereUpdatesOnOtherTabsAreIgnored.has(key)) {
        return;
      }
      const localStorageValue = localStorageGetParsedItem(key);
      const storeState = useStoreBase.getState();

      if (storeState.store[key] !== localStorageValue) {
        storeState.update({[key]: localStorageValue});
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [useStoreBase]);

  const addKeyToIgnoreUpdatesOnOtherTabsList = useCallback((key: string) => {
    keysWhereUpdatesOnOtherTabsAreIgnored.add(key);
  }, []);

  return (
    <LocalStorageContext.Provider
      value={{
        useStoreBase,
        updateLocalStorage,
        addKeyToIgnoreUpdatesOnOtherTabsList,
      }}
    >
      {children}
    </LocalStorageContext.Provider>
  );
}
